/**
 * 罚单管理中心（DSP 侧）类型定义
 * 对应后端 swiftx-base-data-service 罚单表，api-server 通过 Dubbo 透传
 *
 * 空值约定（遵循 CLAUDE.md「JSON 解析规范」）：
 * 非空字段仅 id / penaltyNo / status，其余一律按可空处理，渲染时兜底 '--'
 */

/** 罚单状态（与后端 PartnerPenaltyStatus 枚举一一对应） */
export enum PartnerPenaltyStatus {
    PENDING_SUBMIT = 'PENDING_SUBMIT',
    REVIEWING = 'REVIEWING',
    APPROVED = 'APPROVED',
    REJECTED = 'REJECTED',
    /** 已超时：DSP 超过截止申诉时间未申诉，默认超时认罚 */
    TIMEOUT = 'TIMEOUT',
    /** 待确认：总部审核挂起，后续仍可审核通过或驳回 */
    WAITING = 'WAITING',
    /** 已放弃：DSP 放弃申诉（申诉认罚） */
    ACCEPTED = 'ACCEPTED',
}

/**
 * 页面可见的 Tab，顺序即渲染顺序。
 * 列表查询恒传当前 Tab 的单个 status，因此各 Tab 数据互不串。
 */
export const PENALTY_CENTER_TABS: PartnerPenaltyStatus[] = [
    PartnerPenaltyStatus.PENDING_SUBMIT,
    PartnerPenaltyStatus.REVIEWING,
    PartnerPenaltyStatus.WAITING,
    PartnerPenaltyStatus.APPROVED,
    PartnerPenaltyStatus.REJECTED,
    PartnerPenaltyStatus.TIMEOUT,
    PartnerPenaltyStatus.ACCEPTED,
];

/**
 * appealPartnerPenalty 的 action 入参：后端 PartnerPenaltyStatus 的 value，透传不做转换。
 * 放弃申诉落到「申诉认罚」——即「已放弃」Tab，放弃后该行从「待提交」移到「已放弃」。
 */
export const PENALTY_APPEAL_ACTION = {
    APPEAL: 2,
    ABANDON: 6,
} as const;

/**
 * 罚单已作废 / 状态已被改写（他处已提交、已超时等）：重试必然失败，提示后关闭抽屉并刷新列表。
 */
export const STALE_PENALTY_ERROR_KEYS: readonly string[] = [
    'errors.business.partnerPenalty.voided',
    'errors.business.partnerPenalty.statusNotAllowed',
];

/**
 * 罚单来源（与后端 PenaltySourceType 枚举一一对应）。
 * 固定 5 项，前端写死即可，不需要后端选项接口。
 */
export enum PenaltySource {
    DNR = 'DNR',
    GNC = 'GNC',
    MDB = 'MDB',
    SV = 'SV',
    SP = 'SP',
}

/** 列表项（对应 PartnerPenaltyItemType） */
export interface PartnerPenaltyItem {
    /** 罚单 ID，申诉及查详情用 */
    id: number;
    penaltyNo: string;
    /** 罚单生成时间，ISO-8601 字符串 */
    creationTime?: string | null;
    /** 罚单来源描述，后端拼好直接展示（无 i18n 能力，见 plan §18） */
    penaltySourceDesc?: string | null;
    trackingNumber?: string | null;
    /** 金额，单位美元；GraphQL 侧序列化为字符串，避免精度丢失 */
    amount?: string | null;
    driverId?: number | null;
    driverName?: string | null;
    /** 申诉截止时间，ISO-8601 字符串；为空表示不限期 */
    appealDeadlineAt?: string | null;
    status: PartnerPenaltyStatus;
    /** DSP 名称；仅 warehouse 模式查询该字段，DSP 模式下恒为 undefined */
    dspName?: string | null;
}

/**
 * 操作记录条目（对应后端 AppealOperationLogType，与总部审核侧同构）。
 * 详情查询与独立的 partnerPenaltyOperationLogs 查询返回的是同一个类型，operatedAt 均为 ISO-8601 字符串。
 */
export interface PartnerPenaltyDetailOperationLog {
    id: number;
    operatorName?: string | null;
    /** ISO-8601 字符串 */
    operatedAt?: string | null;
    description?: string | null;
}

/** 罚单详情（对应 PartnerPenaltyDetailType） */
export interface PartnerPenaltyDetail {
    id: number;
    penaltyNo: string;
    creationTime?: string | null;
    penaltySourceDesc?: string | null;
    trackingNumber?: string | null;
    /** 运单对应的订单 ID；为空时运单号不可点击 */
    orderId?: number | null;
    /** 分拣代码；历史罚单可能为空 */
    routeSegmentCode?: string | null;
    amount?: string | null;
    driverId?: number | null;
    driverName?: string | null;
    appealDeadlineAt?: string | null;
    /** 申诉提交时间；仅保存草稿未提交时为空 */
    appealTime?: string | null;
    status: PartnerPenaltyStatus;
    /** 含未提交的草稿理由，用于申诉表单回填 */
    appealReason?: string | null;
    /** 与 evidenceImageUrls 下标一一对应，回传后端用这个而非 URL */
    evidenceObjectKeys: string[];
    evidenceImageUrls: string[];
    rejectReason?: string | null;
    operationLogs: PartnerPenaltyDetailOperationLog[];
    /** DSP 名称；仅 warehouse 模式查询该字段，DSP 模式下恒为 undefined */
    dspName?: string | null;
}

export interface PartnerPenaltyPage {
    content: PartnerPenaltyItem[];
    totalElements: number;
    page: number;
    size: number;
}

/** 各 Tab 计数（一次查询拿齐，避免逐 Tab 多次 RPC） */
export interface PartnerPenaltyStatusCounts {
    pendingSubmit: number;
    reviewing: number;
    approved: number;
    rejected: number;
    waiting: number;
    timeout: number;
    accepted: number;
}

/**
 * 操作记录 Modal 用的条目（对应 partner_operation_log）。
 * 后端只有 description 一列可用，没有独立的 action / remark，故与详情内嵌的条目同构。
 */
export type PartnerPenaltyOperationLog = PartnerPenaltyDetailOperationLog;

/**
 * 列表/计数/导出共用的筛选入参。
 * 注意：**没有 dspId** —— DSP 数据隔离由后端从登录上下文取，前端不可指定，
 * 与 DspQuery.getDspForDspManager() 的既有做法一致。
 */
export interface PartnerPenaltyFiltersTypeInput {
    /** 运单号原始串，后端按 ;/,/换行 拆分（与 reviewTask 口径一致） */
    trackingNumbers?: string | null;
    /** 罚单来源，多个取并集 */
    penaltySources?: PenaltySource[] | null;
    /**
     * 派送司机姓名，**精确匹配**。
     * 不用 driverIds：DSP 侧没有可下拉的司机列表接口，改为用户手打姓名。
     */
    driverName?: string | null;
    /** 前端恒传当前 Tab 对应的单值，不传空数组 */
    statuses?: PartnerPenaltyStatus[] | null;
}

/**
 * 仓库侧筛选入参（对应后端 WarehousePenaltyFiltersTypeInput）。
 * 在共享筛选之上多出 warehouseId（必填，后端据此做仓库权限校验）与 dspId（可选）。
 * 刻意声明为继承关系：warehouse 的入参可直接喂给共享的查询 hook，不用再造一套类型。
 */
export interface WarehousePenaltyFiltersTypeInput extends PartnerPenaltyFiltersTypeInput {
    warehouseId: number;
    /** 为空时查该仓下全部 DSP 的罚单 */
    dspId?: number | null;
}

/** 页面复用模式：DSP 侧可申诉，warehouse 侧只读 */
export type PenaltyCenterMode = 'dsp' | 'warehouse';
